/**
 * A task with subtasks (checkboxes indented under it) as one row with its
 * progress ("To bespreken 2/4"); the chevron shows the subtasks underneath.
 */
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { isOpen } from '@nexus/core';
import type { Task } from '@nexus/workspace';
import { cn } from '../../lib/cn';
import { TaskRow } from './TaskRow';

export function TaskGroup({
  root,
  descendants,
  expanded,
  onToggle,
  projectTitle,
  showStatus,
}: {
  root: Task;
  /** Every subtask under the root, in document order, with its depth (1 = directly under). */
  descendants: { task: Task; depth: number }[];
  expanded: boolean;
  onToggle: () => void;
  projectTitle: (id: string | null) => string | null;
  showStatus?: boolean;
}) {
  const { t } = useTranslation();
  const done = descendants.filter((d) => !isOpen(d.task.status)).length;
  return (
    <div data-testid="task-group">
      <div className="flex items-center">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t('tasks.hideSubtasks', { title: root.title })
              : t('tasks.showSubtasks', { title: root.title })
          }
          className="grid size-6 shrink-0 place-items-center rounded text-fg-muted hover:bg-bg-hover"
        >
          <ChevronRight className={cn('size-4 transition-transform', expanded && 'rotate-90')} />
        </button>
        <div className="min-w-0 flex-1">
          <TaskRow
            task={root}
            projectTitle={projectTitle(root.projectId)}
            showStatus={showStatus}
          />
        </div>
        <span
          className={cn(
            'ml-2 shrink-0 rounded-full px-2 text-xs tabular-nums',
            done === descendants.length
              ? 'bg-green-500/15 text-green-700 dark:text-green-300'
              : 'bg-bg-subtle text-fg-muted',
          )}
          aria-label={t('tasks.subtaskProgress', { done, total: descendants.length })}
        >
          {done}/{descendants.length}
        </span>
      </div>
      {expanded && (
        <div role="group" aria-label={t('tasks.subtasksOf', { title: root.title })}>
          {descendants.map(({ task, depth }) => (
            <div key={task.id} style={{ paddingLeft: 24 + depth * 20 }}>
              <TaskRow
                task={task}
                projectTitle={projectTitle(task.projectId)}
                showStatus={showStatus}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
